import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeWeekly } from '../lib/blip';
import { TAGLINE, WEEKLY_ARCHIVE_URL } from '../lib/site';

// Every claim on this page has to be true of shipped code. Rung 1 describes the
// live meeting tool, rung 2 describes what api/briefblip/blip.php actually does
// (including the verbatim-quote check), and rung 3 describes the double opt-in
// weekly pipeline. Nothing here promises an integration that does not exist.
const RUNGS = [
  {
    index: '01',
    title: 'Meeting blips',
    kicker: 'Recording or transcript',
    body: 'Drop in an audio file or paste a transcript. Out come the decisions, the action items with their owners, and the next steps — exportable as Markdown or as calendar tasks.',
    to: '/meetings',
    cta: 'Brief a meeting',
    external: false,
  },
  {
    index: '02',
    title: 'Doc & URL blips',
    kicker: 'A link or a wall of text',
    body: 'Give it a page or paste the text. You watch it validate, fetch, extract and compose in real time — and every quote is checked against the source before it reaches you.',
    to: '/blip',
    cta: 'Blip a doc',
    external: false,
  },
  {
    index: '03',
    title: 'Weekly briefs',
    kicker: 'One email, every week',
    body: 'Subscribe once and the brief comes to you. Double opt-in, one click to unsubscribe from any issue, and every published issue stays readable on the web.',
    to: WEEKLY_ARCHIVE_URL,
    cta: 'See the archive',
    external: true,
  },
] as const;

export default function Home() {
  return (
    <div className="mx-auto max-w-5xl px-5 pb-4 pt-14 sm:pt-20">
      <section className="animate-rise-in">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-clay">
          Blue Note Logic
        </p>
        <h1 className="wordmark mt-5 max-w-3xl text-[2.6rem] font-bold leading-[1.04] tracking-tight sm:text-6xl">
          {TAGLINE}
          <span className="ml-2 inline-block h-2.5 w-2.5 -translate-y-2 rounded-full bg-clay align-middle motion-safe:animate-pulse" />
        </h1>
        <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-muted">
          A meeting you missed, a filing you will not read, a week of noise. BriefBlip
          turns each of them into something short, sourced, and yours to act on.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link
            to="/blip"
            className="rounded-lg bg-gradient-to-r from-teal to-clay px-6 py-3 font-semibold text-darker shadow-lift transition hover:shadow-glow"
          >
            Blip something now
          </Link>
          <Link
            to="/meetings"
            className="rounded-lg border border-edge bg-panel/70 px-6 py-3 font-semibold text-ink transition hover:border-teal/50 hover:text-teal"
          >
            Brief a meeting
          </Link>
        </div>
      </section>

      <hr className="rule-fade mt-16" />

      <section aria-labelledby="rungs-heading" className="mt-12">
        <h2
          id="rungs-heading"
          className="font-mono text-xs uppercase tracking-[0.24em] text-muted"
        >
          Three ways in
        </h2>

        <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {RUNGS.map((rung) => (
            <li key={rung.index} className="h-full">
              <article className="group flex h-full flex-col rounded-xl border border-edge bg-darker/80 p-6 transition duration-300 hover:-translate-y-0.5 hover:border-teal/40 hover:shadow-lift">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-xs tracking-[0.2em] text-clay">
                    {rung.index}
                  </span>
                  <span className="font-mono text-[0.65rem] uppercase tracking-[0.14em] text-muted">
                    {rung.kicker}
                  </span>
                </div>

                <h3 className="mt-4 font-mono text-xl font-bold tracking-tight text-ink">
                  {rung.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted">{rung.body}</p>

                {rung.external ? (
                  <a
                    href={rung.to}
                    className="mt-6 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-teal transition group-hover:gap-2.5"
                  >
                    {rung.cta}
                    <span aria-hidden="true">&rarr;</span>
                  </a>
                ) : (
                  <Link
                    to={rung.to}
                    className="mt-6 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-teal transition group-hover:gap-2.5"
                  >
                    {rung.cta}
                    <span aria-hidden="true">&rarr;</span>
                  </Link>
                )}
              </article>
            </li>
          ))}
        </ol>
      </section>

      <hr className="rule-fade mt-16" />

      <WeeklyCapture />
    </div>
  );
}

type SignupState = 'idle' | 'sending' | 'sent' | 'error' | 'redirecting';
type Tier = 'free' | 'pro';

const TIERS: { id: Tier; label: string; price: string; blurb: string }[] = [
  { id: 'free', label: 'Free', price: '€0', blurb: 'The EU AI Act Weekly' },
  { id: 'pro', label: 'Pro', price: '€9/mo', blurb: '+ CO2 & Climate, Telecom, Employment law' },
];

/**
 * Post-checkout banner. Stripe redirects here with ?checkout=success|cancelled (see
 * api/briefblip/subscribe.php's createSubscriptionCheckoutSession() call) rather than to a
 * dedicated PHP page — BriefBlip keeps interactive/personalized UI in the SPA, not a second
 * server-rendered surface, for exactly this kind of one-off confirmation state.
 */
function useCheckoutBanner(): 'success' | 'cancelled' | null {
  const [banner, setBanner] = useState<'success' | 'cancelled' | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'cancelled') {
      setBanner(checkout);
      params.delete('checkout');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);
  return banner;
}

function WeeklyCapture() {
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState<Tier>('free');
  const [state, setState] = useState<SignupState>('idle');
  const [error, setError] = useState('');
  const checkoutBanner = useCheckoutBanner();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'sending' || email.trim() === '') return;
    setState('sending');
    setError('');
    try {
      const result = await subscribeWeekly(email.trim(), tier);
      if (result.status === 'checkout' && result.checkoutUrl) {
        setState('redirecting');
        window.location.href = result.checkoutUrl;
        return;
      }
      if (result.status === 'already_pro' && result.manageUrl) {
        setState('redirecting');
        window.location.href = result.manageUrl;
        return;
      }
      setState('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed. Please try again.');
      setState('error');
    }
  };

  return (
    <section
      aria-labelledby="weekly-heading"
      className="mt-12 rounded-xl border border-edge bg-darker/80 p-7 sm:p-9"
    >
      {checkoutBanner === 'success' && (
        <div role="status" className="mb-6 rounded-lg border border-teal/30 bg-teal/[0.07] px-4 py-3 text-sm text-ink-soft">
          <span className="font-semibold text-teal">You&apos;re on BriefBlip Pro.</span> Check your
          email for a receipt and a link to manage your subscription.
        </div>
      )}
      {checkoutBanner === 'cancelled' && (
        <div role="status" className="mb-6 rounded-lg border border-edge bg-white/40 px-4 py-3 text-sm text-ink-soft">
          Checkout cancelled — no charge was made. You can try again any time.
        </div>
      )}

      <div className="sm:flex sm:items-start sm:justify-between sm:gap-10">
        <div className="max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-clay">Rung 03</p>
          <h2 id="weekly-heading" className="mt-3 font-mono text-2xl font-bold tracking-tight text-ink">
            The weekly brief
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            One email a week, briefed the same way everything else here is. Free covers the EU
            AI Act; Pro adds three more regulatory beats to the same issue. We send a
            confirmation link first on the free tier, and every issue carries a one-click
            unsubscribe.
          </p>
        </div>

        <div className="mt-6 w-full max-w-sm sm:mt-1">
          {state === 'sent' ? (
            // 🔴 One message for every valid FREE address. The endpoint deliberately
            // answers identically for a new, pending, or already-subscribed free
            // address so it cannot be used to enumerate subscribers — branching
            // this copy on the response would hand that back. Pro is a distinct,
            // explicit purchase action and is not covered by this guarantee (see
            // subscribe.php's own docblock) — it redirects to Stripe instead of
            // ever reaching this state.
            <div
              role="status"
              className="rounded-lg border border-teal/30 bg-teal/[0.07] px-4 py-4 text-sm leading-relaxed text-ink-soft"
            >
              <p className="font-semibold text-teal">Check your inbox.</p>
              <p className="mt-1">
                If that address can receive it, a confirmation link is on its way. The
                subscription starts once you click it.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate={false}>
              <div role="radiogroup" aria-label="Choose a plan" className="mb-3 grid grid-cols-2 gap-2">
                {TIERS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={tier === t.id}
                    onClick={() => setTier(t.id)}
                    disabled={state === 'sending' || state === 'redirecting'}
                    className={`rounded-lg border px-3 py-2.5 text-left transition disabled:opacity-60 ${
                      tier === t.id
                        ? t.id === 'pro'
                          ? 'border-clay bg-clay/[0.08]'
                          : 'border-teal bg-teal/[0.08]'
                        : 'border-edge bg-white/40 hover:border-teal/40'
                    }`}
                  >
                    <span
                      className={`block font-mono text-[0.65rem] uppercase tracking-[0.14em] ${
                        tier === t.id && t.id === 'pro' ? 'text-clay' : 'text-muted'
                      }`}
                    >
                      {t.label} &middot; {t.price}
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-ink-soft">{t.blurb}</span>
                  </button>
                ))}
              </div>

              <label htmlFor="weekly-email" className="sr-only">
                Email address
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="weekly-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={state === 'sending' || state === 'redirecting'}
                  aria-describedby={state === 'error' ? 'weekly-error' : undefined}
                  placeholder="you@company.com"
                  className="min-w-0 flex-1 rounded-lg border border-edge bg-white/80 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-teal focus:outline-none disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={state === 'sending' || state === 'redirecting' || email.trim() === ''}
                  className="shrink-0 rounded-lg bg-ink px-5 py-3 text-sm font-semibold text-darker transition hover:bg-teal-deep disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {state === 'sending' || state === 'redirecting'
                    ? 'One moment...'
                    : tier === 'pro'
                      ? 'Subscribe — Pro'
                      : 'Subscribe'}
                </button>
              </div>

              {state === 'error' && (
                <p
                  id="weekly-error"
                  role="alert"
                  className="mt-3 rounded-lg border border-clay/40 bg-clay/10 px-3 py-2 text-xs text-[#7a3a1c]"
                >
                  {error}
                </p>
              )}

              <p className="mt-3 font-mono text-xs text-muted">
                {tier === 'pro' ? (
                  <>Billed monthly via Stripe. Cancel any time.</>
                ) : (
                  <>
                    Free.{' '}
                    <a href={WEEKLY_ARCHIVE_URL} className="text-teal hover:underline">
                      Read past issues
                    </a>{' '}
                    before you decide.
                  </>
                )}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
